import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    inject
} from "@angular/core";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";

import { TranslateService } from "@ngx-translate/core";
import _ from "lodash";
import moment from "moment";
import { BusyService } from "~app/common/components/busy/busy.service";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { LagermalDTO, LagermalPeriod } from "~app/common/models/lagermal/LagermalResponseDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { LagermalRequest, LagermalView, LagermalWorkItem, Radtyp } from "../../hantera-lagermal-models";
import { HanteraLagermalService } from "../../hantera-lagermal.service";
import { HanteraLagermalTexts } from "../../hantera-lagermal.texts";
import { LagermalNumericStyledHotCell } from "../lagermal.hot-cell";

@Component({
    selector: "rask-hantera-lagermal-result",
    templateUrl: "./hantera-lagermal-result.component.html",
    styleUrls: ["./hantera-lagermal-result.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [HotDocComponent]
})
export class HanteraLagermalResultComponent implements OnInit, OnChanges {
    activeFilter: LagermalRequest;
    activeView: LagermalView;
    editableRole: boolean;
    hotDoc: HotDoc;
    isInitialized = false;
    lagermalData: LagermalWorkItem[] = [];
    lagermalDataTemp: LagermalDTO[] = [];
    @Input() lagermalFilter: LagermalRequest;
    @Output() lagermalFilterChange: EventEmitter<LagermalRequest> = new EventEmitter<LagermalRequest>();
    matrisIsDirty: boolean;
    months = [
        "jan",
        "feb",
        "mar",
        "apr",
        "maj",
        "jun",
        "jul",
        "aug",
        "sep",
        "okt",
        "nov",
        "dec"
    ];
    saveInProgress = false;
    @Input() selectedView: LagermalView;
    @Output() selectedViewChange: EventEmitter<LagermalView> = new EventEmitter<LagermalView>();

    private readonly $translate = inject(TranslateService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly lagermalService = inject(HanteraLagermalService);
    private readonly modalDialogService = inject(RaskModalService);
    private readonly toasterService = inject(ToastService);

    constructor() {
        this.setupLagermal();
    }

    canSave(): boolean {
        return (this.matrisIsDirty === true);
    }

    changeDefaultValueHS(row: LagermalWorkItem, parentRow: LagermalWorkItem, change: NumericCellChangeArg) {
        const isDelete = change.rawNewValue === null;
        let newValue = change.newValue;
        if (isDelete) {
            if (!parentRow.inherited) {
                newValue = parentRow.standardVarde;
            } else if (row.moStandardVarde) {
                newValue = row.moStandardVarde;
            } else {
                newValue = parentRow.standardVarde;
            }
        }
        row.inherited = isDelete;
        row.toolTip = "";
        row.standardVarde = newValue;
        this.setRowMonthData(row, parentRow, change);
        change.complete();
        this.setDirty();
    }

    changeDefaultValueHSGroup(row: LagermalWorkItem, change: NumericCellChangeArg) {
        const isDelete = change.rawNewValue === null;
        let newValue = change.newValue;
        row.inherited = isDelete;
        if (isDelete) {
            newValue = row.moStandardVarde;
        }
        row.standardVarde = newValue;
        // Sätter alla värden på handelssortimentsgrupp-cellerna
        this.setRowMonthData(row, row, change);

        (row.lagermal ?? []).forEach((l: LagermalWorkItem) => {
            if (l.standardVarde === null || l.inherited) {
                if (isDelete && l.inherited) {
                    if (l.moStandardVarde) {
                        l.standardVarde = l.moStandardVarde;
                    } else {
                        l.standardVarde = newValue;
                    }
                    l.standardVardeOnSave = true;
                } else {
                    l.standardVarde = newValue;
                    l.inherited = true;
                }
                l.toolTip = "";
                this.setRowMonthData(l, row, change);
            }
        });

        change.complete();
        this.setDirty();
    }

    changeMonthValueHS(propertyName: string, row: LagermalWorkItem, parentRow: LagermalWorkItem, change: NumericCellChangeArg) {
        const isDelete = change.rawNewValue === null;
        let newValue = change.newValue;
        if (isDelete) {
            // Om explicit standardvärde finns på samma rad, ta det
            // I annat fall ta värdet rakt av från standardvärdet för handelssortimentgsgruppen (översta cellen i samma kolumn)
            if (!row.inherited) {
                newValue = row.standardVarde;
            } else if (parentRow[propertyName].overridden) {
                newValue = parentRow[propertyName].lagermal;
            } else {
                newValue = row.standardVarde;
            }
        }

        row[propertyName].overridden = !isDelete;
        row[propertyName].toolTip = "";
        row[propertyName].lagermal = newValue;
        change.complete();
        this.setDirty();
    }

    changeMonthValueHSGroup(propertyName: string, row: LagermalWorkItem, change: NumericCellChangeArg) {
        const isDelete = change.rawNewValue === null;
        const newValue = isDelete ? row.standardVarde : change.newValue;

        row[propertyName].lagermal = newValue;
        row[propertyName].overridden = !isDelete;

        (row.lagermal ?? []).forEach((l) => {
            if (l.standardVarde === null || l.inherited) {
                const lmo: any = l[propertyName];
                if (lmo.overridden) {
                    return;
                }
                if (isDelete) {
                    if (l.standardVarde) {
                        lmo.lagermal = l.standardVarde;
                    } else {
                        lmo.lagermal = newValue;
                    }
                } else {
                    lmo.lagermal = newValue;
                }

                lmo.toolTip = "";
            }
        });
        change.complete();
        this.setDirty();
    }

    changeQuantityHS(propertyName: string, period: LagermalPeriod, parentRow: LagermalWorkItem, change: NumericCellChangeArg) {
        const newValue = change.newValue;
        period.kvantitet = newValue;
        period.overridden = true;
        const parentValue = _.sumBy(parentRow.lagermal, (p) => p[propertyName].kvantitet);
        parentRow[propertyName].kvantitet = parentValue;
        change.complete();
        this.setDirty();
    }

    clearDirty() {
        this.matrisIsDirty = false;
    }

    createHSCell(propertyName: string, lagermal: LagermalWorkItem, lagermalHSG: LagermalWorkItem, view: LagermalView): LagermalNumericStyledHotCell {
        const enterQuantity = lagermal.enterQuantity;
        const inPoLevel = (this.activeFilter.produktionsomradeId > 0);
        const inMoLevel = !inPoLevel;
        const isProduktGrupp = (lagermal.lagermalTyp === Radtyp.produktgrupp);
        const isDefaultValue = (propertyName === "standardVarde");
        const period = lagermal[propertyName];

        let func: (newValue: NumericCellChangeArg) => void;
        if (!inMoLevel && enterQuantity) {
            if (!isProduktGrupp) {
                func = change => this.changeQuantityHS(propertyName, period, lagermalHSG, change);
            }
        } else if (isDefaultValue) {
            if (isProduktGrupp) {
                func = change => this.changeDefaultValueHSGroup(lagermal, change);
            } else {
                func = change => this.changeDefaultValueHS(lagermal, lagermalHSG, change);
            }
        } else {
            if (isProduktGrupp) {
                func = change => this.changeMonthValueHSGroup(propertyName, lagermal, change);
            } else {
                func = change => this.changeMonthValueHS(propertyName, lagermal, lagermalHSG, change);
            }
        }

        const val = (isDefaultValue ? lagermal[propertyName] : this.getMonthValue(period, view));

        const flags: Cellstyle = this.getCustomFlags(propertyName, lagermal, period, view);
        const classList = this.getCustomCellStyles(propertyName, lagermal, period, view);

        return new LagermalNumericStyledHotCell(val, classList, flags, func);
    }

    createRowHS(doc: HotDoc, parentRow: HotRow, lagermalHS: LagermalWorkItem, lagermalHSG: LagermalWorkItem, view: LagermalView) {
        doc.createChildRow(parentRow, row => {
            row.addText("handelssortimentsgrupp", "", Cellstyle.Readonly);
            row.addText("handelssortiment", lagermalHS.handelssortiment, Cellstyle.Readonly);
            this.createRowMonth(row, lagermalHS, lagermalHSG, view);
        });
    }

    createRowHSG(doc: HotDoc, lagermalHSG: LagermalWorkItem, view: LagermalView): HotRow {
        return doc.createRow(row => {
            row.addText("handelssortimentsgrupp", lagermalHSG.handelssortimentsgrupp, Cellstyle.Readonly);
            row.addText("handelssortiment", "", Cellstyle.Readonly);
            this.createRowMonth(row, lagermalHSG, null, view);
        });
    }

    createRowMonth(row: HotRow, lagermal: LagermalWorkItem, lagermalHSG: LagermalWorkItem, view: LagermalView) {
        if (view === LagermalView.Dagar) {
            row.add("standardVarde", this.createHSCell("standardVarde", lagermal, lagermalHSG, view));
        }

        this.months.forEach((month) => {
            row.add(month, this.createHSCell(month, lagermal, lagermalHSG, view));
        });
    }

    dirtyCheckCallback(result: IDirtyFormResult): void {
        // Krav från dirtyhandlern
    }

    getMonthName(month: string): string {
        const str = `LAGERMAL_DETAIL_MATRIS_HEAD_${month.toUpperCase()}`;
        return this.$translate.instant(str);
    }

    getColumns = (): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];
        columns.push({
            header: this.$translate.instant("LAGERMAL_DETAIL_MATRIS_HEAD_HANDELSSORTIMENTSGRUPP"),
            cellName: "handelssortimentsgrupp",
            width: 180
        });

        columns.push({
            header: this.$translate.instant("LAGERMAL_DETAIL_MATRIS_HEAD_HANDELSSORTIMENT"),
            cellName: "handelssortiment",
            width: 150
        });

        if (this.activeView === LagermalView.Dagar) {
            columns.push({
                header: this.$translate.instant("LAGERMAL_DETAIL_MATRIS_HEAD_STANDARDVARDE"),
                cellName: "standardVarde",
                type: "numeric",
                width: 100
            });
        }

        this.months.forEach((month) => {
            columns.push({
                header: this.getMonthName(month),
                cellName: month,
                type: "numeric",
                width: 80
            });
        });

        return columns;
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    getCustomCellStyles(propertyName: string, rowdata: LagermalWorkItem, period: LagermalPeriod, view: LagermalView): string[] {
        const classList: string[] = [];
        if (this.isReadonly(propertyName, rowdata, period, view)) {
            classList.push("lagermal-readonly");
        }

        if (propertyName === "standardVarde") {
            if (rowdata.inherited) {
                classList.push("lagermal-inherited");
            }
            if (!rowdata.enterQuantity && rowdata.lagermalTyp === Radtyp.produktgrupp) {
                classList.push("lagermal-standard-hsg");
            }
        } else {
            if (view === LagermalView.Dagar) {
                if (period && period.overridden) {
                    classList.push("lagermal-overridden");
                } else {
                    classList.push("lagermal-normal");
                }
            } else {
                if (period && period.kvantitet > 0) {
                    classList.push("lagermal-overridden");
                } else {
                    classList.push("lagermal-normal");
                }
            }
        }
        return classList;
    }

    getCustomFlags(propertyName: string, rowdata: LagermalWorkItem, period: LagermalPeriod, view: LagermalView): Cellstyle {
        let flags: Cellstyle = null;
        if (this.isReadonly(propertyName, rowdata, period, view)) {
            // eslint-disable-next-line no-bitwise
            flags = flags | Cellstyle.Readonly;
        }
        return flags;
    }

    getMonthValue(period: LagermalPeriod, view: LagermalView): number {
        if (view === LagermalView.Dagar) {
            return period.lagermal;
        } else {
            return period.kvantitet;
        }
    }

    getNewLagermal(filter: LagermalRequest) {
        if (this.matrisIsDirty) {
            this.modalDialogService.confirm(new ModalSettings(HanteraLagermalTexts.texts.changeDirtyHeader,HanteraLagermalTexts.texts.changeDirtyBody)).subscribe(
                {
                    next: result => {
                        // "save" är inte save, det är att man klickar på Ja i confirm-rutan
                        if (result === ConfirmDialogResponse.save) {
                            this.loadNewLagermal(filter);
                        } else {
                            this.lagermalFilterChange.emit(this.activeFilter);
                            this.selectedViewChange.emit(this.activeView);
                        }
                    },
                    error: (error) => {
                        this.lagermalFilterChange.emit(this.activeFilter);
                        this.selectedViewChange.emit(this.activeView);
                    }
                }
            );
        } else {
            this.loadNewLagermal(filter);
        }
    }

    getRows(doc: HotDoc) {
        const view = this.activeView;

        this.lagermalData.forEach((lagermalHSG) => {
            const parentRow = this.createRowHSG(doc, lagermalHSG, view);

            if (lagermalHSG.lagermal && lagermalHSG.lagermal.length > 0) {
                lagermalHSG.lagermal.forEach((lagermalHS => {
                    this.createRowHS(doc, parentRow, lagermalHS as LagermalWorkItem, lagermalHSG, view);
                }));
            }
        });
    }

    initLagermalData(lagermalData: LagermalWorkItem[]) {
        this.lagermalData = lagermalData;

        this.clearDirty();

        this.hotDoc.rebind();
    }

    initMatrisSettings() {
        const colFactory = () => this.getColumns();

        const rowFactory = (doc: HotDoc) => this.getRows(doc);

        const hotDocSettings: IHotDocSettings = {
            raskSettings: {
                calculateSum: false
            },
            nestedRows: true,
            fixedColumnsStart: 2
        };

        this.hotDoc = new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    }

    isReadonly(propertyName: string, rowdata: LagermalWorkItem, period: LagermalPeriod, view: LagermalView) {
        const editableRole = this.editableRole;
        if (propertyName === "standardVarde") {
            return (editableRole === false) || rowdata.enterQuantity;
        } else {
            const inPoLevel = (this.activeFilter.produktionsomradeId > 0);
            const inMoLevel = !inPoLevel;
            const enterQuantity = (period.enterQuantity);
            const showDays = (view === LagermalView.Dagar);
            const hasPassed = period.hasPassed;

            const readyOnlyQuantity = (rowdata.lagermalTyp === Radtyp.produktgrupp) && enterQuantity ||
                (!showDays && !enterQuantity) ||
                (showDays && enterQuantity) ||
                (enterQuantity && inMoLevel);
            return readyOnlyQuantity || hasPassed || (editableRole === false);
        }
    }

    loadNewLagermal(filter: LagermalRequest) {
        if (filter.marknadsomradeId || filter.produktionsomradeId) {
            this.busyService.addWork(this.lagermalService.getLagermal(filter.marknadsomradeId, filter.produktionsomradeId, moment(filter.period).toDate())).subscribe(result => {
                this.activeFilter = filter;
                this.activeView = this.selectedView;
                this.initLagermalData(result.lagermal as LagermalWorkItem[]);
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.isInitialized) {
            return;
        }
        if (changes.lagermalFilter) {
            if (changes.lagermalFilter.currentValue !== this.activeFilter) {
                this.getNewLagermal(changes.lagermalFilter.currentValue);
            }

        } else if (changes.selectedView) {
            if (changes.selectedView.currentValue !== this.activeView) {
                if (this.matrisIsDirty) {
                    this.getNewLagermal(this.activeFilter);
                } else {
                    this.activeView = changes.selectedView.currentValue;
                    this.hotDoc.rebind();
                }
            }
        }
    }

    ngOnInit(): void {
        this.editableRole = this.authenticationService.canEdit();

        this.dirtyForm.onDirtyNgModel(
            this.canSave.bind(this),
            this.save.bind(this),
            d => this.dirtyCheckCallback(d),
            () => this.canSave(),
            HanteraLagermalTexts.texts.leaveDirtyHeader,
            HanteraLagermalTexts.texts.leaveDirtyBody,
            {
                size: "lg",
                centered: false
            });

        this.isInitialized = true;
        if (this.lagermalFilter) {
            this.getNewLagermal(this.lagermalFilter);
        }
    }

    save() {

        if (this.canSave()) {
            if (!this.saveInProgress) {
                this.saveLagermal();
            }
        }
    }

    saveLagermal() {
        const lagerData = this.lagermalData.filter((lm) => lm.lagermalTyp === Radtyp.produktgrupp);
        this.saveInProgress = true;
        this.busyService.addWork(this.lagermalService.saveLagermal(this.activeFilter.marknadsomradeId,
            this.activeFilter.produktionsomradeId,
            lagerData,
            this.activeFilter.period as any))
            .subscribe({
                next: (result) => {
                    this.toasterService.add("DEFAULT_CHANGE_SUCCESS", 0, 2500);
                    this.initLagermalData(result.lagermal as LagermalWorkItem[]);
                },
                complete: () => {
                    this.saveInProgress = false;
                }
            });
    }

    setDirty() {
        this.matrisIsDirty = true;
        setTimeout(() => this.hotDoc.rebind(), 10);
    }

    setRowMonthData(row: LagermalWorkItem, parentRow: LagermalWorkItem, change: NumericCellChangeArg) {
        let value = change.newValue;
        const isDelete = change.rawNewValue === null;
        this.months.forEach((month) => {
            if (row[month].hasPassed || row[month].overridden) {
                return;
            }
            row[month].standardVardeOnSave = isDelete;

            if (!row.inherited) {
                value = row.standardVarde;
            } else if (parentRow[month].overridden) {
                value = parentRow[month].lagermal;
            } else {
                value = row.standardVarde;
            }
            row[month].lagermal = value;
        });
    }

    setupLagermal() {
        this.initMatrisSettings();
    }
}
